/**
 * Admin page (`/admin`) — visible only to the ADMIN_EMAIL user (the nav item is
 * gated by `me.is_admin`, and every API call 403s for anyone else).
 *
 * One destructive operation: "Datenbank zurücksetzen" wipes all finance data
 * (bank connections, accounts, transactions, imports, budgets, rules) and
 * restores the default bucket taxonomy; user accounts and the current session
 * survive. The button stays disabled until the confirmation phrase is typed —
 * mirroring the backend, which rejects any other `confirm` value.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, Trash2 } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { ErrorState } from '../components/ui/ErrorState.tsx';
import { Field } from '../components/ui/Field.tsx';
import { Input } from '../components/ui/Input.tsx';
import { SegmentedControl } from '../components/ui/SegmentedControl.tsx';
import { useOnlineStatus } from '../context/OnlineStatusContext.tsx';
import { useToast } from '../context/ToastContext.tsx';
import { api } from '../lib/api.ts';
import { useCurrentUser } from '../lib/hooks/useCurrentUser.ts';
import { useReceiptSettings, useUpdateReceiptSettings } from '../lib/hooks/useReceipts.ts';
import type { ReceiptExtractionEngine } from '../lib/types.ts';

/** Must match the backend's RESET_CONFIRM_PHRASE. */
export const RESET_CONFIRM_PHRASE = 'ZURÜCKSETZEN';

interface AdminResetOut {
  deleted: Record<string, number>;
}

const EXTRACTION_ENGINE_OPTIONS: { value: ReceiptExtractionEngine; label: string }[] = [
  { value: 'local', label: 'Lokal (OCR)' },
  { value: 'claude', label: 'Claude API' },
];

export default function AdminPage() {
  const user = useCurrentUser();
  const { toast } = useToast();
  const { guardOnline } = useOnlineStatus();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState('');
  const [lastResult, setLastResult] = useState<AdminResetOut | null>(null);

  const receiptSettings = useReceiptSettings();
  const updateReceiptSettings = useUpdateReceiptSettings();

  const reset = useMutation({
    mutationFn: () =>
      api.post<AdminResetOut>('/api/admin/reset-database', { confirm }),
    onSuccess: (result) => {
      setLastResult(result);
      setConfirm('');
      // Everything the app has cached is gone — refetch the world.
      void qc.invalidateQueries();
      toast('Datenbank wurde zurückgesetzt.', { variant: 'success' });
    },
    onError: (err) => toast(err.message, { variant: 'error' }),
  });

  if (!user.is_admin) {
    return (
      <div>
        <PageHeader title="Admin" />
        <div className="w-full px-4 py-6 sm:px-6">
          <ErrorState
            title="Kein Zugriff"
            message="Diese Seite ist nur für Administratoren verfügbar."
          />
        </div>
      </div>
    );
  }

  const armed = confirm === RESET_CONFIRM_PHRASE;
  const deletedRows = lastResult
    ? Object.values(lastResult.deleted).reduce((sum, n) => sum + n, 0)
    : 0;

  return (
    <div>
      <PageHeader title="Admin" />
      <div className="w-full max-w-2xl space-y-6 px-4 py-6 sm:px-6">
        <section className="space-y-4 rounded-lg border-l-4 border-rose-500 bg-rose-50 p-5 dark:bg-rose-950/40">
          <div className="flex items-start gap-3">
            <ShieldAlert
              className="h-6 w-6 shrink-0 text-rose-600 dark:text-rose-400"
              aria-hidden
            />
            <div className="space-y-1">
              <h2 className="font-semibold text-rose-900 dark:text-rose-200">
                Datenbank zurücksetzen
              </h2>
              <p className="text-sm text-rose-800/90 dark:text-rose-200/80">
                Löscht unwiderruflich alle Bankverbindungen, Konten, Buchungen,
                Importe, Budgets, Regeln und Transfers. Die Kategorien werden auf
                den Standard zurückgesetzt; Benutzerkonten und Anmeldungen bleiben
                erhalten.
              </p>
            </div>
          </div>

          <Field label={`Zur Bestätigung „${RESET_CONFIRM_PHRASE}“ eingeben`}>
            {(id) => (
              <Input
                id={id}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={RESET_CONFIRM_PHRASE}
                autoComplete="off"
              />
            )}
          </Field>

          <Button
            variant="danger"
            disabled={!armed || reset.isPending}
            onClick={() => reset.mutate()}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            {reset.isPending ? 'Wird zurückgesetzt …' : 'Unwiderruflich zurücksetzen'}
          </Button>
        </section>

        {lastResult ? (
          <section className="rounded-lg bg-slate-50 p-5 text-sm text-slate-600 dark:bg-slate-900 dark:text-slate-300">
            <p className="font-medium text-slate-900 dark:text-slate-100">
              Zurücksetzen abgeschlossen
            </p>
            <p>
              {deletedRows} Zeilen in {Object.keys(lastResult.deleted).length}{' '}
              Tabellen gelöscht. Die Standard-Kategorien wurden neu angelegt.
            </p>
          </section>
        ) : null}

        <section className="space-y-3 rounded-lg bg-slate-50 p-5 dark:bg-slate-900">
          <h2 className="font-semibold text-slate-900 dark:text-slate-100">
            Beleg-Erkennung
          </h2>

          {receiptSettings.isPending ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Lädt …</p>
          ) : receiptSettings.isError ? (
            <ErrorState
              title="Einstellungen konnten nicht geladen werden"
              error={receiptSettings.error}
              onRetry={receiptSettings.refetch}
            />
          ) : receiptSettings.data ? (
            <>
              <div
                className={
                  updateReceiptSettings.isPending ? 'pointer-events-none opacity-60' : ''
                }
              >
                <SegmentedControl
                  ariaLabel="Beleg-Erkennung"
                  value={receiptSettings.data.extraction_engine}
                  onChange={(value) => {
                    if (updateReceiptSettings.isPending) return;
                    if (!guardOnline()) return;
                    updateReceiptSettings.mutate(value);
                  }}
                  options={EXTRACTION_ENGINE_OPTIONS}
                />
              </div>

              <div className="space-y-1 text-sm text-slate-500 dark:text-slate-400">
                {!receiptSettings.data.tesseract_available ? (
                  <p>
                    Tesseract ist auf dem Server nicht installiert – lokale Erkennung
                    liefert keine Ergebnisse.
                  </p>
                ) : null}
                {!receiptSettings.data.claude_available ? (
                  <p>
                    ANTHROPIC_API_KEY ist nicht gesetzt – Claude API nicht verfügbar.
                  </p>
                ) : (
                  <p>Modell: {receiptSettings.data.claude_model}</p>
                )}
              </div>

              {updateReceiptSettings.isError ? (
                <p className="text-sm text-rose-600 dark:text-rose-400">
                  {updateReceiptSettings.error.message}
                </p>
              ) : null}
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
}
